import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { SortableTh } from '../../../components/common/SortableTh';
import { ListPager, ListSearch, useListControls } from '../../../components/common/listControls';
import { useFormatters } from '../../../contexts/PreferencesContext';
import type {
  DrilldownDeliveryLine,
  DrilldownDeliverySupplier,
  DrilldownQualityLine,
  DrilldownQualitySupplier,
} from '../../../lib/api';

const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

function Flag({ value }: { value: boolean | null }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  return value ? (
    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900">Yes</Badge>
  ) : (
    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900">No</Badge>
  );
}

function Empty({ cols, testId }: { cols: number; testId: string }) {
  return (
    <TableRow>
      <TableCell colSpan={cols} className="py-8 text-center text-sm text-muted-foreground" data-testid={testId}>
        Nothing matches.
      </TableCell>
    </TableRow>
  );
}

interface DeliverySuppliersProps {
  rows: DrilldownDeliverySupplier[];
  target: number;
  onPick: (id: number) => void;
}

/** A month's suppliers, worst on-time first; click one to filter the dashboard to it. */
export function DeliverySuppliersTable({ rows, target, onPick }: DeliverySuppliersProps) {
  const list = useListControls(rows, {
    searchText: (r) => r.name,
    sorters: { name: (r) => r.name.toLowerCase(), lines: (r) => r.lines, late: (r) => r.late_lines, on_time: (r) => r.on_time, otif: (r) => r.otif, required: (r) => r.required },
    defaultSort: { key: 'on_time', dir: 'asc' },
    tieBreak: (r) => r.name,
  });
  const th = (f: string, l: string, a: 'left' | 'right' = 'right') => (
    <SortableTh field={f} sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align={a}>{l}</SortableTh>
  );
  return (
    <div className="space-y-3">
      <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search suppliers…" testId="drilldown-suppliers-search" />
      <Table>
        <TableHeader>
          <TableRow>{th('name', 'Supplier', 'left')}{th('lines', 'Lines')}{th('late', 'Late lines')}{th('on_time', 'On time')}{th('otif', 'OTIF')}{th('required', 'Required date')}</TableRow>
        </TableHeader>
        <TableBody>
          {list.pageRows.map((r) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => onPick(r.id)} data-testid={`drilldown-supplier-${r.id}`}>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell className="text-right tabular-nums">{r.lines.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">{r.late_lines.toLocaleString()}</TableCell>
              <TableCell className={`text-right tabular-nums ${r.on_time !== null && r.on_time < target ? 'text-red-700 dark:text-red-300 font-medium' : ''}`}>{pct(r.on_time)}</TableCell>
              <TableCell className="text-right tabular-nums">{pct(r.otif)}</TableCell>
              <TableCell className="text-right tabular-nums">{pct(r.required)}</TableCell>
            </TableRow>
          ))}
          {list.pageRows.length === 0 && <Empty cols={6} testId="drilldown-suppliers-empty" />}
        </TableBody>
      </Table>
    </div>
  );
}

/** The delivery lines behind the month, latest-arriving first. */
export function DeliveryLinesTable({ rows }: { rows: DrilldownDeliveryLine[] }) {
  const { formatDate } = useFormatters();
  const d = (v: string | null) => (v ? formatDate(v) : '—');
  const list = useListControls(rows, {
    searchText: (r) => [r.supplier, r.division, r.po_number, r.site_plot].join(' '),
    sorters: {
      supplier: (r) => r.supplier.toLowerCase(), division: (r) => r.division.toLowerCase(), po: (r) => r.po_number,
      required: (r) => r.required_date, agreed: (r) => r.agreed_date, delivered: (r) => r.delivered_date, late: (r) => r.days_late,
    },
    defaultSort: { key: 'late', dir: 'desc' },
    tieBreak: (r) => r.po_number,
  });
  const th = (f: string, l: string, a: 'left' | 'right' = 'left') => (
    <SortableTh field={f} sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align={a}>{l}</SortableTh>
  );
  return (
    <div className="space-y-3">
      <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search supplier, division, PO or plot…" testId="drilldown-lines-search" />
      <ListPager list={list} position="top" noun="lines" testIdPrefix="drilldown-lines" />
      <Table>
        <TableHeader>
          <TableRow>
            {th('supplier', 'Supplier')}{th('division', 'Division')}{th('po', 'PO number')}
            {th('required', 'Required')}{th('agreed', 'Agreed')}{th('delivered', 'Delivered')}{th('late', 'Days late', 'right')}
            <TableHead className="text-center">On time</TableHead>
            <TableHead className="text-center">OTIF</TableHead>
            <TableHead className="text-center">Met required</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.pageRows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-medium">{r.supplier}</TableCell>
              <TableCell className="text-muted-foreground">{r.division}</TableCell>
              <TableCell className="tabular-nums">{r.po_number}{r.site_plot ? <span className="block text-xs text-muted-foreground">{r.site_plot}</span> : null}</TableCell>
              <TableCell className="whitespace-nowrap">{d(r.required_date)}</TableCell>
              <TableCell className="whitespace-nowrap">{d(r.agreed_date)}</TableCell>
              <TableCell className="whitespace-nowrap">{d(r.delivered_date)}</TableCell>
              <TableCell className={`text-right tabular-nums ${r.days_late !== null && r.days_late > 0 ? 'text-red-700 dark:text-red-300' : ''}`}>{r.days_late ?? '—'}</TableCell>
              <TableCell className="text-center"><Flag value={r.on_time} /></TableCell>
              <TableCell className="text-center"><Flag value={r.otif} /></TableCell>
              <TableCell className="text-center"><Flag value={r.required} /></TableCell>
            </TableRow>
          ))}
          {list.pageRows.length === 0 && <Empty cols={10} testId="drilldown-lines-empty" />}
        </TableBody>
      </Table>
      <ListPager list={list} position="bottom" noun="lines" testIdPrefix="drilldown-lines" />
    </div>
  );
}

interface QualitySuppliersProps {
  rows: DrilldownQualitySupplier[];
  onPick: (id: number) => void;
}

/** A month's quality by supplier, most care cases first. */
export function QualitySuppliersTable({ rows, onPick }: QualitySuppliersProps) {
  const list = useListControls(rows, {
    searchText: (r) => r.name,
    sorters: { name: (r) => r.name.toLowerCase(), care: (r) => r.care_cases, ncrs: (r) => r.ncrs, quantity: (r) => r.quantity, lines: (r) => r.lines },
    defaultSort: { key: 'care', dir: 'desc' },
    tieBreak: (r) => r.name,
  });
  const th = (f: string, l: string, a: 'left' | 'right' = 'right') => (
    <SortableTh field={f} sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align={a}>{l}</SortableTh>
  );
  return (
    <div className="space-y-3">
      <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search suppliers…" testId="drilldown-quality-suppliers-search" />
      <Table>
        <TableHeader>
          <TableRow>{th('name', 'Supplier', 'left')}{th('care', 'Care cases')}{th('ncrs', 'NCRs')}{th('quantity', 'Quantity delivered')}{th('lines', 'Rows')}</TableRow>
        </TableHeader>
        <TableBody>
          {list.pageRows.map((r) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => onPick(r.id)} data-testid={`drilldown-quality-supplier-${r.id}`}>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell className="text-right tabular-nums">{r.care_cases.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">{r.ncrs.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">{r.quantity.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">{r.lines.toLocaleString()}</TableCell>
            </TableRow>
          ))}
          {list.pageRows.length === 0 && <Empty cols={5} testId="drilldown-quality-suppliers-empty" />}
        </TableBody>
      </Table>
    </div>
  );
}

/** The quality rows behind the month. */
export function QualityLinesTable({ rows }: { rows: DrilldownQualityLine[] }) {
  const { formatDate } = useFormatters();
  const list = useListControls(rows, {
    searchText: (r) => [r.supplier, r.ncr_number, r.category, r.severity, r.status, r.description].join(' '),
    sorters: { supplier: (r) => r.supplier.toLowerCase(), date: (r) => r.issue_date, care: (r) => r.care_cases, quantity: (r) => r.quantity },
    defaultSort: { key: 'care', dir: 'desc' },
    tieBreak: (r) => r.supplier,
  });
  const th = (f: string, l: string, a: 'left' | 'right' = 'left') => (
    <SortableTh field={f} sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align={a}>{l}</SortableTh>
  );
  return (
    <div className="space-y-3">
      <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search supplier, NCR, category…" testId="drilldown-quality-lines-search" />
      <ListPager list={list} position="top" noun="rows" testIdPrefix="drilldown-quality-lines" />
      <Table>
        <TableHeader>
          <TableRow>
            {th('supplier', 'Supplier')}{th('date', 'Date')}
            <TableHead>NCR</TableHead><TableHead>Category</TableHead><TableHead>Severity</TableHead>
            {th('care', 'Care cases', 'right')}{th('quantity', 'Quantity', 'right')}
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.pageRows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-medium">{r.supplier}</TableCell>
              <TableCell className="whitespace-nowrap">{formatDate(r.issue_date)}</TableCell>
              <TableCell>{r.ncr_number || '—'}</TableCell>
              <TableCell>{r.category || '—'}</TableCell>
              <TableCell>{r.severity || '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{r.care_cases.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">{r.quantity.toLocaleString()}</TableCell>
            </TableRow>
          ))}
          {list.pageRows.length === 0 && <Empty cols={7} testId="drilldown-quality-lines-empty" />}
        </TableBody>
      </Table>
      <ListPager list={list} position="bottom" noun="rows" testIdPrefix="drilldown-quality-lines" />
    </div>
  );
}
